/**
 * Users Management Page
 * Adapted from HappyHippo Users pattern
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Button, Modal, TextInput, Select, Badge } from 'flowbite-react';
import { HiPlus, HiTrash, HiSearch, HiRefresh } from 'react-icons/hi';
import { useStatus } from '../contexts/StatusContext';
import { userApi, CompanyMember } from '../api/user';
import { partnerOrganizationsApi, CompanyContext } from '../api/partnerOrganizations';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (email: string, role: string, billing: 'self_pays' | 'admin_pays') => Promise<void>;
  hasSubscription: boolean;
  tierPrice?: number;
}

const InviteModal: React.FC<InviteModalProps> = ({ isOpen, onClose, onInvite, hasSubscription, tierPrice }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [billing, setBilling] = useState<'self_pays' | 'admin_pays'>('self_pays');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await onInvite(email, role, billing);
      setEmail('');
      setRole('member');
      setBilling('self_pays');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite user');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={isOpen} onClose={onClose}>
      <Modal.Header>Invite Team Member</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-700 bg-red-100 rounded-md">{error}</div>
          )}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <TextInput
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@company.com"
              required
            />
          </div>
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <Select id="role" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Billing
            </label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="billing"
                  value="self_pays"
                  checked={billing === 'self_pays'}
                  onChange={() => setBilling('self_pays')}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">They pay for their own seat</p>
                  <p className="text-xs text-gray-500">Invitee subscribes and pays independently</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${!hasSubscription ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <input
                  type="radio"
                  name="billing"
                  value="admin_pays"
                  checked={billing === 'admin_pays'}
                  onChange={() => setBilling('admin_pays')}
                  disabled={!hasSubscription}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Add to my subscription{tierPrice ? ` — $${tierPrice}/mo per seat` : ''}
                  </p>
                  <p className="text-xs text-gray-500">
                    {hasSubscription
                      ? 'License added to your bill immediately'
                      : 'Requires an active subscription'}
                  </p>
                </div>
              </label>
            </div>
          </div>
          <div className="flex justify-end space-x-2 pt-4">
            <Button color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Inviting...' : 'Send Invite'}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

const CompanyUsers: React.FC<{ company: CompanyContext }> = ({ company }) => {
  const { user, client, canAddCollaborator } = useStatus();
  const [users, setUsers] = useState<CompanyMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const companyId = company.company_id;
  const statusMatchesCompany = client?.client_id === company.client_id;
  const localCapacityAllows = !statusMatchesCompany || canAddCollaborator();

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setUsers([]);
    if (!companyId) {
      setError('No organization is associated with your account.');
      setIsLoading(false);
      return;
    }
    try {
      const response = await userApi.listUsers(companyId);
      if (response.success && response.data) setUsers(response.data);
      else setError(response.error || 'Failed to load team members');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load team members');
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  const canManageMembers = company.admin === true && users.some(member => member.email === user?.email && member.role === 'admin');

  const handleInvite = async (email: string, role: string, billing: 'self_pays' | 'admin_pays') => {
    const response = await userApi.inviteUser(companyId, email.trim().toLowerCase(), role, billing);
    if (!response.success) {
      throw new Error(response.error || 'Failed to invite user');
    }

    await loadUsers();
  };

  const handleRemoveUser = async (email: string) => {
    if (!confirm(`Are you sure you want to remove ${email} from the team?`)) {
      return;
    }

    try {
      const response = await userApi.removeUser(companyId, email);
      if (response.success) {
        setUsers(users.filter((u) => u.email !== email));
      } else {
        setError(response.error || 'Failed to remove user');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove user');
    }
  };

  const safeUsers = Array.isArray(users) ? users : [];
  const filteredUsers = safeUsers.filter((u) =>
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.display_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team Members</h1>
          <p className="mt-1 text-sm text-gray-500">
            {companyId ? `Organization: ${company.company_name}` : 'Organization unavailable'}
          </p>
        </div>
        <Button
          onClick={() => setIsInviteModalOpen(true)}
          disabled={!companyId || !canManageMembers || !localCapacityAllows}
        >
          <HiPlus className="mr-2 h-4 w-4" />
          Invite Member
        </Button>
      </div>

      {/* Upgrade prompt if at limit */}
      {!localCapacityAllows && (
        <Card className="bg-amber-50 border-amber-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-amber-800">Team limit reached</h3>
              <p className="text-sm text-amber-700">
                Upgrade your plan to add more team members.
              </p>
            </div>
            <a
              href="/subscriptions"
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
            >
              Upgrade
            </a>
          </div>
        </Card>
      )}

      {/* Error display */}
      {error && (
        <div className="p-4 text-red-700 bg-red-100 rounded-md">{error}</div>
      )}

      {/* Search */}
      <Card>
        <div className="mb-4 flex items-center gap-3">
          <Button color="gray" onClick={loadUsers} disabled={isLoading} aria-label="Refresh team members">
            <HiRefresh className="h-4 w-4" />
          </Button>
          <TextInput
            icon={HiSearch}
            placeholder="Search team members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Users Table */}
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mindmeld-primary"></div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {error ? 'Team membership unavailable' : searchQuery ? 'No users match your search' : 'No team members yet'}
          </div>
        ) : (
          <Table>
            <Table.Head>
              <Table.HeadCell>User</Table.HeadCell>
              <Table.HeadCell>Role</Table.HeadCell>
              <Table.HeadCell>Billing</Table.HeadCell>
              <Table.HeadCell>Joined</Table.HeadCell>
              <Table.HeadCell>Updated</Table.HeadCell>
              <Table.HeadCell>Actions</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {filteredUsers.map((member) => {
                const isOwner = member.email === client?.owner_email;
                const isCurrentUser = member.email === user?.email;

                return (
                  <Table.Row key={member.email} className="bg-white">
                    <Table.Cell>
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {(member.display_name || member.email).charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {member.display_name || member.email.split('@')[0]}
                          </p>
                          <p className="text-sm text-gray-500">{member.email}</p>
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge color={isOwner ? 'purple' : member.role === 'admin' ? 'warning' : 'blue'}>
                        {isOwner ? 'Owner' : member.role === 'admin' ? 'Admin' : 'Member'}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge color={member.billing_type === 'admin_paid' ? 'indigo' : 'gray'}>
                        {member.billing_type === 'admin_paid' ? 'Licensed' : 'Own sub'}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-sm text-gray-500">
                        {member.created_at ? new Date(member.created_at).toLocaleDateString() : 'Pending account'}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-sm text-gray-500">
                        {member.last_active
                          ? new Date(member.last_active).toLocaleDateString()
                          : 'Not available'}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex space-x-2">
                        <button
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Remove user"
                          onClick={() => handleRemoveUser(member.email)}
                          disabled={!canManageMembers || isOwner || isCurrentUser}
                        >
                          <HiTrash className="h-4 w-4" />
                        </button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}
      </Card>

      {/* Invite Modal */}
      <InviteModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={handleInvite}
        hasSubscription={statusMatchesCompany && !!client?.stripe_subscription_id}
        tierPrice={statusMatchesCompany ? (client?.subscription_tier === 'professional' ? 149 : 49) : undefined}
      />
    </div>
  );
};

const Users: React.FC = () => {
  const [companies, setCompanies] = useState<CompanyContext[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    setCompanies([]); setCompanyId(''); setLoading(true); setError(null);
    try {
      const response = await partnerOrganizationsApi.contexts();
      if (!response.success || !response.data) { setError(response.error || 'Unable to load organizations'); return; }
      setCompanies(response.data);
      if (response.data.length === 1) setCompanyId(response.data[0].company_id);
    } catch { setError('Unable to load organizations'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const selected = companies.find(c => c.company_id === companyId);
  return <div className="space-y-4">
    <label htmlFor="team-company">Team organization</label>
    <select id="team-company" value={companyId} disabled={loading} onChange={e => setCompanyId(e.target.value)}>
      <option value="">Select organization</option>
      {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.company_name}</option>)}
    </select>
    <button onClick={refresh} disabled={loading}>Refresh organizations</button>
    {error && <p role="alert">{error}</p>}
    {!selected && !loading && !error && <p>Select an organization to view its team.</p>}
    {selected && <CompanyUsers key={selected.company_id} company={selected} />}
  </div>;
};

export default Users;
